import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  ThemeProvider, 
  Box,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Modal,
  Backdrop,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { catTheme, styles } from './GuideTheme';

// Import the markdown content directly
import markdownPath from './content/StrayCatAdoption.md';

// Helper function to get images for subsections
function getSubsectionImage(sectionNumber) {
  try {
    return require(`./images/StrayCatAdoption/${sectionNumber.replace('.', '-')}.png`);
  } catch (e) {
    try {
      return require(`./images/not_found.png`); // fallback image
    } catch (e) {
      return 'https://via.placeholder.com/300x200?text=Image+Not+Found'; // online fallback
    }
  }
}

const StrayCatAdoptionPage = ({ title }) => {
  const [markdownContent, setMarkdownContent] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [sections, setSections] = useState([]);
  const [introContent, setIntroContent] = useState('');
  const [conclusionContent, setConclusionContent] = useState('');
  const [subSections, setSubSections] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSubsection, setSelectedSubsection] = useState(null);

  useEffect(() => {
    // Fetch the markdown content
    fetch(markdownPath)
      .then(response => response.text())
      .then(text => {
        setMarkdownContent(text);
        
        // Extract section titles for tabs
        const sectionRegex = /^## \*\*(\d+)\\. (.+?)\*\*/gm;
        const matches = [...text.matchAll(sectionRegex)];
        
        const extractedSections = matches.map(match => ({
          number: match[1],
          title: match[2],
          position: match.index
        }));
        
        setSections(extractedSections);

        // Extract introduction content (first section)
        if (extractedSections.length > 0) {
          const introSection = extractedSections[0];
          const nextSection = extractedSections[1];
          const introStartPos = introSection.position;
          const introEndPos = nextSection ? nextSection.position : text.length;
          setIntroContent(text.substring(introStartPos, introEndPos));
        }

        // Extract conclusion content (last section)
        if (extractedSections.length > 0) {
          const conclusionSection = extractedSections[extractedSections.length - 1];
          const conclusionStartPos = conclusionSection.position;
          setConclusionContent(text.substring(conclusionStartPos));
        }

        // Extract subsections (like 2.1, 2.2, etc.)
        const subSectionRegex = /^### \*\*(\d+\.\d+) (.+?)\*\*/gm;
        const subMatches = [...text.matchAll(subSectionRegex)];
        
        const extractedSubSections = {};
        subMatches.forEach(match => {
          const mainSection = match[1].split('.')[0];
          if (!extractedSubSections[mainSection]) {
            extractedSubSections[mainSection] = [];
          }
          
          // Find the end position of this subsection
          const currentPosition = match.index;
          let endPosition;
          
          // Find the next subsection or section
          const nextSubMatch = subMatches.find(m => m.index > currentPosition);
          const nextSectionMatch = matches.find(m => m.index > currentPosition);
          
          if (nextSubMatch && (!nextSectionMatch || nextSubMatch.index < nextSectionMatch.index)) {
            endPosition = nextSubMatch.index;
          } else if (nextSectionMatch) {
            endPosition = nextSectionMatch.index;
          } else {
            endPosition = text.length;
          }
          
          extractedSubSections[mainSection].push({
            number: match[1],
            title: match[2],
            position: currentPosition,
            endPosition: endPosition,
            content: text.substring(currentPosition, endPosition)
          });
        });
        
        setSubSections(extractedSubSections);
      })
      .catch(error => {
        console.error('Error loading markdown file:', error);
        setMarkdownContent('Error loading content. Please try again later.');
      });
  }, []);
  
  const handleOpenModal = (subsection) => {
    setSelectedSubsection(subsection);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Get section description for the current tab
  const getTabDescription = () => {
    if (sections.length === 0 || !markdownContent) return '';
    
    // Skip the first section (intro) and last section (conclusion)
    // We need to add 1 to tabValue because the first tab (index 0) should show the second section (index 1)
    const sectionIndex = tabValue + 1;
    
    // For tabs
    const currentSection = sections[sectionIndex];
    const nextSection = sections[sectionIndex + 1];
    
    if (!currentSection) return '';
    
    const startPos = currentSection.position;
    const endPos = nextSection ? nextSection.position : 
                  (sections[sections.length - 1].number === '9') ? 
                  sections[sections.length - 1].position : markdownContent.length;
    
    return markdownContent.substring(startPos, endPos);
  };

  // Custom renderers for markdown components
  const renderers = {
    h1: ({ children }) => (
      <Typography variant="h4" gutterBottom color="primary" sx={styles.pageTitle}>
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography variant="h6" color="secondary" gutterBottom sx={{ borderLeft: '3px solid #a26438', pl: 1.5, py: 0.5, mt: 3, mb: 2 }}>
        {children}
      </Typography>
    ),
    h3: ({ children }) => {
      // For modal content, render h3 normally
      if (modalOpen) {
        return (
          <Typography variant="subtitle1" color="secondary" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            {children}
          </Typography>
        );
      }
      
      // Skip rendering h3 in the main content as we'll display them as cards
      return null;
    },
    p: ({ children }) => (
      <Typography variant="body1" color="primary" paragraph sx={{ fontSize: '0.9rem', lineHeight: 1.6, mb: 1 }}>
        {children}
      </Typography>
    ),
    ul: ({ children }) => (
      <Box sx={{ pl: 2, mb: 2 }}>
        {children}
      </Box>
    ),
    li: ({ children }) => (
      <Typography variant="body1" color="primary" sx={{ fontSize: '0.9rem', lineHeight: 1.6, display: 'flex', alignItems: 'flex-start', mb: 1 }}>
        <Box component="span" sx={{ color: '#a26438', mr: 1, fontWeight: 'bold' }}>•</Box>
        <Box>{children}</Box>
      </Typography>
    ),
    table: ({ children }) => (
      <TableContainer component={Paper} sx={{ mb: 3, overflow: 'hidden' }}>
        <Table size="small" sx={{ fontSize: '0.9rem' }}>
          {children}
        </Table>
      </TableContainer>
    ),
    thead: ({ children }) => (
      <TableHead sx={{ backgroundColor: 'rgba(162,100,56,0.1)' }}>
        {children}
      </TableHead>
    ),
    tbody: ({ children }) => (
      <TableBody>
        {children}
      </TableBody>
    ),
    tr: ({ children }) => (
      <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(162,100,56,0.03)' } }}>
        {children}
      </TableRow>
    ),
    th: ({ children }) => (
      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', padding: '8px 16px', borderBottom: '1px solid rgba(162,100,56,0.2)' }}>
        {children}
      </TableCell>
    ),
    td: ({ children }) => (
      <TableCell sx={{ fontSize: '0.8rem', padding: '8px 16px', borderBottom: '1px solid rgba(162,100,56,0.1)' }}>
        {children}
      </TableCell>
    ),
    blockquote: ({ children }) => (
      <Box sx={{ borderLeft: '3px solid #a26438', pl: 2, py: 1, my: 2, bgcolor: 'rgba(162,100,56,0.05)' }}>
        {children}
      </Box>
    ),
    strong: ({ children }) => (
      <Box component="span" sx={{ fontWeight: 'bold', color: '#3C210E' }}>
        {children}
      </Box>
    ),
    em: ({ children }) => (
      <Box component="span" sx={{ fontStyle: 'italic' }}>
        {children}
      </Box>
    ),
    hr: () => <Divider sx={{ my: 2 }} />
  };

  // Render subsections as cards
  const renderSubsectionCards = () => {
    // We need to add 1 to tabValue because the first tab (index 0) should show the second section (index 1)
    const sectionIndex = tabValue + 1;
    const currentSectionNumber = sections[sectionIndex]?.number;
    const currentSubsections = currentSectionNumber ? subSections[currentSectionNumber] || [] : [];
    
    return (
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {currentSubsections.map((subsection, index) => (
          <Grid item xs={6} sm={4} md={3} key={index}>
            <Card 
              sx={{ 
                cursor: 'pointer', 
                transition: 'transform 0.2s', 
                '&:hover': { transform: 'scale(1.02)' },
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={() => handleOpenModal(subsection)}
            >
              <CardMedia
                component="img"
                height="140"
                image={getSubsectionImage(subsection.number)}
                alt={subsection.title}
                sx={{ objectFit: 'cover' }}
              />
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Typography variant="subtitle1" color="secondary" sx={{ fontWeight: 600, textAlign: 'center' }}>
                  {subsection.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <ThemeProvider theme={catTheme}>
      <div style={styles.container}>
        <Typography variant="h4" gutterBottom color="primary" sx={styles.pageTitle}>
          {title}
        </Typography>
        
        {/* Introduction content - displayed above tabs */}
        {introContent && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 1, boxShadow: '0px 2px 4px rgba(0,0,0,0.05)' }}>
              <ReactMarkdown 
                components={{
                  ...renderers,
                  h2: () => null, // Skip the h2 title for introduction
                }}
                remarkPlugins={[remarkGfm]}
              >
                {introContent}
              </ReactMarkdown>
            </Box>
          </Box>
        )}
        
        {/* Tabs for navigation - excluding intro and conclusion */}
        <Paper sx={{ mb: 2 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              minHeight: '36px', 
              '& .MuiTab-root': { 
                minHeight: '36px', 
                fontSize: '0.8rem',
                py: 0.5
              } 
            }}
          >
            {sections.slice(1, -1).map((section, index) => (
              <Tab key={index} label={`${section.title.split('：')[0]}`} />
            ))}
          </Tabs>
        </Paper>
        
        {/* Section description */}
        <Typography variant="body2" color="primary" paragraph sx={{ fontSize: '0.85rem', lineHeight: 1.5, mb: 2 }}>
          {sections[tabValue + 1]?.title.includes('：') ? 
            sections[tabValue + 1]?.title.split('：')[1] : 
            ''
          }
        </Typography>
        
        {/* Tab content - display subsection cards */}
        {renderSubsectionCards()}
        
        {/* Subsection detail modal */}
        <Modal
          open={modalOpen}
          onClose={handleCloseModal}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={modalOpen}>
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: '80%' },
              maxWidth: 900,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 3,
              borderRadius: 2,
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              {selectedSubsection && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" color="secondary" sx={{ fontWeight: 600 }}>
                      {selectedSubsection.title}
                    </Typography>
                    <CloseIcon 
                      onClick={handleCloseModal} 
                      sx={{ cursor: 'pointer', color: 'text.secondary' }} 
                    />
                  </Box>
                  
                  <Grid container spacing={3}>
                    {/* Image on the left */}
                    <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <img 
                        src={getSubsectionImage(selectedSubsection.number)} 
                        alt={selectedSubsection.title}
                        style={{ 
                          width: '100%', 
                          maxHeight: '350px', 
                          objectFit: 'cover', 
                          borderRadius: '8px' 
                        }}
                      />
                    </Grid>
                    
                    {/* Content on the right */}
                    <Grid item xs={12} md={7}>
                      <Box>
                        <ReactMarkdown 
                          components={{
                            ...renderers,
                            // Skip rendering the h3 title since it's already displayed as h5 above
                            h3: () => null
                          }}
                          remarkPlugins={[remarkGfm]}
                        >
                          {selectedSubsection.content}
                        </ReactMarkdown>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          </Fade>
        </Modal>
        
        {/* Conclusion content - displayed at the end */}
        {conclusionContent && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 1, boxShadow: '0px 2px 4px rgba(0,0,0,0.05)' }}>
              <ReactMarkdown 
                components={{
                  ...renderers,
                  h2: () => null, // Skip the h2 title for conclusion
                }}
                remarkPlugins={[remarkGfm]}
              >
                {conclusionContent}
              </ReactMarkdown>
            </Box>
          </Box>
        )}
      </div>
    </ThemeProvider>
  );
};

export default StrayCatAdoptionPage;
